import { create } from 'zustand';

/**
 * Consent state for the GDPR cookie/tracking flow. Split into a tiny store (scope + the stored decision)
 * plus pure decision helpers in `consentPolicy.ts`. Only non-essential analytics (Hotjar) is gated;
 * strictly-necessary cookies (Cognito auth/session) need no consent and are never toggled here.
 *
 * Behavioral rules (see the plan): geo runs ASYNC and never blocks the UI; a user who already decided
 * under the current POLICY_VERSION is never re-asked and the geo lookup is skipped entirely.
 */

/** Bump to re-prompt everyone after a material change to what/how we track (see `hasValidConsent`). */
export const POLICY_VERSION = '2026-07-19';

/** The persisted decision. `analytics` = may we load Hotjar. */
export interface StoredConsent {
  version: string;
  analytics: boolean;
  /** ISO timestamp of the decision (proof-of-consent; mirrored to the BE for signed-in users in F4). */
  decidedAt: string;
}

/** Resolution phase of the async geo check. `pending` until it resolves; `unknown` collapses to out-of-scope. */
export type GeoScope = 'pending' | 'in-scope' | 'out-of-scope';

const STORAGE_KEY = 'journal:consent.v1';

/** Read the prior decision synchronously (so a returning user never re-sees the banner / re-runs geo). */
export function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<StoredConsent>;
    if (p && typeof p.version === 'string' && typeof p.analytics === 'boolean' && typeof p.decidedAt === 'string') {
      return { version: p.version, analytics: p.analytics, decidedAt: p.decidedAt };
    }
  } catch { /* corrupt/blocked storage → treat as no decision */ }
  return null;
}

function writeStoredConsent(c: StoredConsent | null): void {
  try {
    if (c) localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* private mode / quota — non-fatal, the in-memory store still holds this session's choice */ }
}

interface ConsentStore {
  scope: GeoScope;
  /** null = no decision yet under any version. */
  consent: StoredConsent | null;
  /** Whether the granular preferences dialog is open (lifted here so the banner, Cookie Policy, and
   * Settings can all reopen it — the dialog is mounted once in `PrivacyLayer`). */
  prefsOpen: boolean;
  setScope: (scope: GeoScope) => void;
  /** User made a choice (banner / preferences). Persists + records the timestamp against POLICY_VERSION. */
  decide: (analytics: boolean) => StoredConsent;
  /** Clear the decision (used by "Withdraw"/reset in preferences) so the flow can run again. */
  withdraw: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
}

export const useConsentStore = create<ConsentStore>((set) => ({
  scope: 'pending',
  consent: readStoredConsent(),
  prefsOpen: false,
  setScope: (scope) => set({ scope }),
  decide: (analytics) => {
    const c: StoredConsent = { version: POLICY_VERSION, analytics, decidedAt: new Date().toISOString() };
    writeStoredConsent(c);
    set({ consent: c });
    return c;
  },
  withdraw: () => { writeStoredConsent(null); set({ consent: null }); },
  openPreferences: () => set({ prefsOpen: true }),
  closePreferences: () => set({ prefsOpen: false }),
}));
